/* ScreenNavigator.tsx — OPTIONAL add-on. */

import { useCallback, useEffect, useMemo, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface ScreenDef {
  id: string;
  title: string;
  section?: string;
  route: string;
  /** Optional reference design for this screen: an image URL or a page/prototype URL. */
  wireframe?: string;
  authRequired?: boolean;
  disabledReason?: string;
}

export interface ScreenNavigatorProps {
  screens: ScreenDef[];
  /** Current host route/path. */
  route: string;
  /** Navigate the host app to a screen's route. */
  onNavigate: (route: string) => void;
  /** localStorage namespace for visited + collapsed state. Default 'fbtk-nav'. */
  storageKey?: string;
  /** Launcher pill position. Default 'left'. */
  position?: 'left' | 'right';
}

function loadSet(key: string): Set<string> {
  try {
    const v = localStorage.getItem(key);
    return new Set(v ? (JSON.parse(v) as string[]) : []);
  } catch {
    return new Set();
  }
}
function saveSet(key: string, s: Set<string>): void {
  try {
    localStorage.setItem(key, JSON.stringify([...s]));
  } catch {
    /* ignore */
  }
}

function matchScreen(screens: ScreenDef[], route: string): ScreenDef | null {
  let best: ScreenDef | null = null;
  let bestLen = -1;
  for (const s of screens) {
    if (s.route === route) return s;
    if (s.route !== '/' && route.startsWith(s.route) && s.route.length > bestLen) {
      best = s;
      bestLen = s.route.length;
    }
  }
  return best;
}

function isImage(url: string): boolean {
  return /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(url);
}

export function ScreenNavigator({
  screens,
  route,
  onNavigate,
  storageKey = 'fbtk-nav',
  position = 'left',
}: ScreenNavigatorProps) {
  const LS_VISITED = `${storageKey}-visited`;
  const LS_COLLAPSED = `${storageKey}-collapsed`;

  const [open, setOpen] = useState(false);
  const [wireOpen, setWireOpen] = useState(false);
  const [q, setQ] = useState('');
  const [visited, setVisited] = useState<Set<string>>(() => loadSet(LS_VISITED));
  const [collapsed, setCollapsed] = useState<Set<string>>(() => loadSet(LS_COLLAPSED));

  const current = useMemo(() => matchScreen(screens, route), [screens, route]);

  // Mark current screen visited
  useEffect(() => {
    if (!current) return;
    setVisited((prev) => {
      if (prev.has(current.id)) return prev;
      const next = new Set(prev);
      next.add(current.id);
      saveSet(LS_VISITED, next);
      return next;
    });
  }, [current, LS_VISITED]);

  // Close panels on Escape
  useEffect(() => {
    if (!open && !wireOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setWireOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, wireOpen]);

  const sections = useMemo(() => {
    const order: string[] = [];
    const byId: Record<string, ScreenDef[]> = {};
    for (const s of screens) {
      const sec = s.section || 'Screens';
      if (!byId[sec]) {
        byId[sec] = [];
        order.push(sec);
      }
      byId[sec].push(s);
    }
    return order.map((label) => ({ label, items: byId[label] }));
  }, [screens]);

  const toggleSection = useCallback(
    (label: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(label)) next.delete(label);
        else next.add(label);
        saveSet(LS_COLLAPSED, next);
        return next;
      });
    },
    [LS_COLLAPSED],
  );

  const matches = (s: ScreenDef) => {
    if (!q) return true;
    return `${s.id} ${s.title} ${s.section || ''}`.toLowerCase().includes(q.toLowerCase());
  };

  const jump = (s: ScreenDef) => {
    if (s.disabledReason) return;
    onNavigate(s.route);
    setOpen(false);
  };

  const visibleScreens = useMemo(() => screens.filter(matches), [screens, q]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearchKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const first = visibleScreens.find((s) => !s.disabledReason);
    if (first) jump(first);
  };

  if (!screens.length) return null;

  return (
    <div className="fbtk-root">
      {/* Launcher pills */}
      <div className="fbtk-nav-launch" style={position === 'right' ? { left: 'auto', right: 22 } : undefined}>
        <button
          className={open ? 'on' : ''}
          onClick={() => setOpen((v) => !v)}
          title="Browse screens"
          aria-label="Browse screens"
        >
          ⛶ Screens
          {current && <span className="crumb-id">{current.title}</span>}
        </button>
        {current?.wireframe && (
          <button
            className={wireOpen ? 'on' : ''}
            onClick={() => setWireOpen((v) => !v)}
            title="Show the wireframe this screen was built from"
            aria-label="Show wireframe"
          >
            ▢ Wireframe
          </button>
        )}
      </div>

      {/* rail */}
      {open && (
        <>
          <div className="fbtk-rail-scrim" onClick={() => setOpen(false)} />
          <aside className="proto-rail">
            <div className="pr-search">
              <input
                type="text"
                placeholder="Find a screen… (Enter jumps to first match)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onSearchKeyDown}
                autoFocus
              />
              <span className="pr-count">
                {visibleScreens.length}/{screens.length}
              </span>
            </div>
            {sections.map((sec) => {
              const items = sec.items.filter(matches);
              if (q && !items.length) return null;
              const isCollapsed = collapsed.has(sec.label);
              return (
                <div key={sec.label} className={'pr-section' + (isCollapsed ? ' collapsed' : '')}>
                  <div className="pr-section-h" onClick={() => toggleSection(sec.label)}>
                    {sec.label}
                    <span className="chev">▾</span>
                  </div>
                  <div className="pr-list">
                    {items.map((s) => (
                      <button
                        key={s.id}
                        className={
                          'pr-item' +
                          (current?.id === s.id ? ' active' : '') +
                          (visited.has(s.id) ? ' visited' : '') +
                          (s.disabledReason ? ' disabled' : '')
                        }
                        disabled={!!s.disabledReason}
                        title={s.disabledReason ?? (s.authRequired ? 'Requires an authenticated session' : undefined)}
                        onClick={() => jump(s)}
                      >
                        <span className="dot" />
                        <span className="pr-label">{s.title}</span>
                        {s.authRequired && <span className="auth-flag" title="Requires an authenticated session">AUTH</span>}
                        {s.wireframe && <span className="wire-flag" title="Has wireframe">▢</span>}
                        {s.disabledReason && <span className="token-flag" title={s.disabledReason}>TOKEN</span>}
                        <span className="id">{s.id}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </aside>
        </>
      )}

      {/* Wireframe reference */}
      {wireOpen && current && (
        <div className="fbtk-wire">
          <div className="fbtk-wire-head">
            <div>
              <div className="fbtk-wire-title">{current.title}</div>
              <div className="fbtk-wire-sub">
                {current.id} · {current.route}
              </div>
            </div>
            {current.wireframe && (
              <a
                className="fbtk-wire-open"
                href={current.wireframe}
                target="_blank"
                rel="noreferrer"
              >
                Open ↗
              </a>
            )}
            <button className="fbtk-wire-close" onClick={() => setWireOpen(false)} aria-label="Close wireframe">
              ×
            </button>
          </div>
          <div className="fbtk-wire-body">
            {current.wireframe ? (
              isImage(current.wireframe) ? (
                <img src={current.wireframe} alt={`${current.title} wireframe`} />
              ) : (
                <iframe src={current.wireframe} title={`${current.title} wireframe`} />
              )
            ) : (
              <div className="fbtk-wire-empty">No wireframe mapped for this screen.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
